import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import TurnstileWidget from '../components/TurnstileWidget';

export default function AdminLogin() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState(null);
    const { login } = useAdminAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const result = await login({ email, password, turnstile_token: turnstileToken });
            
            if (result.success) {
                // Redirection automatique vers le dashboard admin
                window.location.href = '/admin';
            } else {
                setError(result.error);
            }
        } catch (error) {
            setError(t('adminLogin.loginError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-8">
                {/* Logo et titre */}
                <div className="text-center">
                    <img src={namkeLogoHeader} alt="namke" className="h-16 w-auto mx-auto mb-4 object-contain" />
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        {t('adminLogin.title')}
                    </h2>
                    <p className="text-gray-600">
                        {t('adminLogin.subtitle')}
                    </p>
                </div>

                {/* Formulaire de connexion */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('adminLogin.emailAddress')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)] focus:border-[rgb(5,70,37)] transition-colors"
                                    placeholder="admin@namke.app"
                                />
                            </div>
                        </div>

                        {/* Mot de passe */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('adminLogin.password')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)] focus:border-[rgb(5,70,37)] transition-colors"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Message d'erreur */}
                        {error && (
                            <div className="form-message form-message--error p-4">
                                <p className="text-sm">{error}</p>
                            </div>
                        )}

                        <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                        {/* Bouton de connexion */}
                        <button
                            type="submit"
                            disabled={loading || (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken)}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[rgb(239,76,35)] hover:bg-[rgb(200,58,28)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(239,76,35)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('adminLogin.connecting')}
                                </div>
                            ) : (
                                t('adminLogin.login')
                            )}
                        </button>
                    </form>

                    {/* Liens utiles */}
                    <div className="mt-6 text-center">
                        <a href="/" className="text-sm font-medium text-[rgb(5,70,37)] hover:text-[rgb(239,76,35)] transition-colors">
                            {t('adminLogin.backToHome')}
                        </a>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center text-sm text-gray-900 font-normal">
                    <p>{t('adminLogin.rights')}</p>
                </div>
            </div>
        </div>
    );
} 